/**
 * contributionBreakdown
 *
 * Pure function that calculates the 7-step contribution breakdown
 * based on Spanish legal brackets for employment pension plans.
 *
 * Brackets (Art. 5.3 RDL 1/2020):
 *   - Bracket 1 (company <= 500): employee can contribute up to company * 2.5
 *   - Bracket 2 (500 < company <= 1500): 1,250 + 0.25 * (company - 500)
 *   - Bracket 3 (company > 1500): employee can contribute up to company * 1
 */

import { CONTRIBUTION_LIMITS } from './contributionLimits';

export interface ContributionBreakdownInput {
	companyAnnualCommitted: number;
	totalCompanyContributions: number;
	totalParticipantContributions: number;
	individualExtraAportations: number;
	recommendedMonthlyContribution: number;
	currentMonth: number; // 1-12
	currentDay: number;
	contributionDay: number;
	grossAnnualSalary?: number;
	// TODO: WORKAROUND — companyThisYearCompanyCommitted del backend es YTD, no anual comprometido.
	// Cuando el backend se arregle para devolver el anual real, eliminar monthlyCompanyContribution
	// y usar directamente companyAnnualCommitted.
	// Ref: testing marzo 2026, caso antonio@tuio.com (Coconut Ventures)
	monthlyCompanyContribution?: number;
	contributionPeriodicity?: string;
	isFreelance?: boolean;
}

export interface ContributionBreakdownOutput {
	step1_companyAnnual: number;
	step2_bracketLimit: number;
	step2_bracketMultiplier: string;
	step3_individualSlot: number;
	step4_maxCombined: number;
	step5_alreadyContributed: number;
	step6_remainingBudget: number;
	step7_remainingMonths: number;
	recommendedMonthly: number;
	activeBracket: 1 | 2 | 3;
	highSalaryOverride: boolean;
}

// resolveEmployerContribution returns monthly for most paths (COLLECTIVE_AGREEMENT,
// CATEGORY_BRACKET, SENIORITY_BRACKET, GROSS_SALARY_PERCENTAGE).
// For FIXED_AMOUNT it returns raw amount in period units without conversion.
// Use contributionPeriodicity to annualize correctly in all cases.
function annualizeContribution(amount: number, periodicity?: string): number {
	switch (periodicity) {
		case 'YEARLY':
			return amount;
		case 'SEMI_ANNUALLY':
			return amount * 2;
		case 'QUARTERLY':
			return amount * 4;
		case 'BI_MONTHLY':
			return amount * 6;
		default:
			return amount * 12; // MONTHLY or unspecified
	}
}

export function calculateContributionBreakdown(
	input: ContributionBreakdownInput
): ContributionBreakdownOutput {
	const {
		companyAnnualCommitted,
		totalCompanyContributions,
		totalParticipantContributions,
		individualExtraAportations,
		currentMonth,
		currentDay,
		contributionDay,
		monthlyCompanyContribution,
		contributionPeriodicity,
		isFreelance,
		grossAnnualSalary
	} = input;

	// Remaining months (calculated first — needed for annual projection)
	const step7_remainingMonths = Math.max(
		1,
		currentDay > contributionDay ? 12 - currentMonth : 12 - currentMonth + 1
	);

	// Step 1: Projected company annual (YTD + remaining × monthly rate), capped at legal limit
	// Autónomo (isFreelance) → cap reducido 4.250; empleado → 8.500
	const maxEmployerAnnual = isFreelance
		? CONTRIBUTION_LIMITS.FREELANCE_PLAN
		: CONTRIBUTION_LIMITS.EMPLOYEE_PLAN;

	const rawCompanyAnnual =
		monthlyCompanyContribution !== undefined && monthlyCompanyContribution !== null
			? totalCompanyContributions +
				(annualizeContribution(monthlyCompanyContribution, contributionPeriodicity) / 12) *
					step7_remainingMonths
			: companyAnnualCommitted;

	const step1_companyAnnual = Math.min(rawCompanyAnnual, maxEmployerAnnual);

	// Step 2: Bracket limit based on company contribution
	let step2_bracketLimit: number;
	let step2_bracketMultiplier: string;
	let activeBracket: 1 | 2 | 3;

	// Art. 5.3 RDL 1/2020: salary > 60K → employee can contribute up to company × 1
	const highSalaryOverride = (grossAnnualSalary ?? 0) > CONTRIBUTION_LIMITS.HIGH_SALARY_THRESHOLD;

	if (highSalaryOverride) {
		activeBracket = 3;
		step2_bracketLimit = step1_companyAnnual;
		step2_bracketMultiplier = '\u00d7 1';
	} else if (step1_companyAnnual <= 500) {
		activeBracket = 1;
		step2_bracketLimit = step1_companyAnnual * 2.5;
		step2_bracketMultiplier = '\u00d7 2,5';
	} else if (step1_companyAnnual <= 1500) {
		activeBracket = 2;
		step2_bracketLimit = 1250 + 0.25 * (step1_companyAnnual - 500);
		step2_bracketMultiplier = '1.250 + 25%';
	} else {
		activeBracket = 3;
		step2_bracketLimit = step1_companyAnnual;
		step2_bracketMultiplier = '\u00d7 1';
	}

	// Step 3: Individual slot (1500 minus external plan contributions)
	const step3_individualSlot = Math.max(
		0,
		CONTRIBUTION_LIMITS.INDIVIDUAL_PLAN - individualExtraAportations
	);

	// Step 4: Max combined annual (no cap — backend enforces global 10000 limit)
	const step4_maxCombined = step2_bracketLimit + step3_individualSlot;

	// Step 5: Already contributed this year (participant only)
	const step5_alreadyContributed = totalParticipantContributions;

	// Step 6: Remaining budget
	const step6_remainingBudget = Math.max(0, step4_maxCombined - step5_alreadyContributed);

	// Step 7: already calculated at the top

	return {
		step1_companyAnnual,
		step2_bracketLimit,
		step2_bracketMultiplier,
		step3_individualSlot,
		step4_maxCombined,
		step5_alreadyContributed,
		step6_remainingBudget,
		step7_remainingMonths,
		recommendedMonthly: Math.round((step6_remainingBudget / step7_remainingMonths) * 100) / 100,
		activeBracket,
		highSalaryOverride
	};
}
